import { Badge } from "antd";
import CategoryHeader from "../../components/category/CategoryHeader";
import { 
  SearchOutlined, 
  CarOutlined, 
  WalletOutlined,
  CheckCircleOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  StarOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";

export const HowItWorks = ({ isDarkMode }) => {
  const steps = [
    {
      step: "01",
      icon: <SearchOutlined className="text-xl" />,
      title: "Check Price",
      description: "Enter phone details & get instant quote",
      features: ["Instant valuation", "AI-powered pricing", "No hidden fees"],
      color: "from-blue-500 to-cyan-500",
      bgColor: isDarkMode ? "bg-blue-900/30" : "bg-blue-50",
    },
    {
      step: "02",
      icon: <CarOutlined className="text-xl" />,
      title: "Free Pickup",
      description: "Free doorstep pickup at your preferred time",
      features: ["Free doorstep service", "Flexible timing", "Trained professionals"],
      color: "from-green-500 to-emerald-500",
      bgColor: isDarkMode ? "bg-green-900/30" : "bg-green-50",
    },
    {
      step: "03",
      icon: <WalletOutlined className="text-xl" />,
      title: "Get Paid",
      description: "Instant payment as soon as device is picked up",
      features: ["Instant bank transfer", "Cash on pickup", "100% secure"],
      color: "from-orange-500 to-red-500",
      bgColor: isDarkMode ? "bg-orange-900/30" : "bg-orange-50",
    },
  ];

  return (
    <div className="container px-4 py-8 mx-auto max-w-7xl">
      {/* Using same CategoryHeader component */}
      <CategoryHeader
        title="How Tap2Cash Works"
        subtitle="Three simple steps to get instant cash for your old phone"
        brandCount={3}
        isDarkMode={isDarkMode}
      />
      
      <div className="grid gap-4 mt-6 md:grid-cols-3">
        {steps.map((step, idx) => (
          <div key={idx} className="relative">
            {/* Connector Line */}
            {idx < steps.length - 1 && (
              <div className="hidden md:block absolute top-1/3 left-full w-full h-0.5 bg-gradient-to-r from-gray-200 to-gray-300  -translate-y-1/2 z-0">
                <ArrowRightOutlined className="absolute right-0 text-xs text-gray-400 -translate-y-1/2 top-1/2 " />
              </div>
            )}
            
            {/* Compact Card */}
            <div className={`relative z-10 p-4 rounded-xl transition-all duration-300 ${step.bgColor} ${isDarkMode ? 'hover:bg-opacity-40' : 'hover:shadow-md'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              {/* Icon Circle */}
              <div className="mb-3">
                <div className={`w-12 h-12 bg-gradient-to-r ${step.color} rounded-lg flex items-center justify-center text-white shadow-sm`}>
                  {step.icon}
                </div>
              </div>
              
              {/* Step Number */}
              <div className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full mb-2 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <span className={`font-mono text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  STEP {step.step}
                </span>
              </div>
              
              {/* Title */}
              <h3 className={`text-sm font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {step.title}
              </h3>
              
              {/* Description */}
              <p className={`text-xs mb-3 leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {step.description}
              </p>
              
              {/* Features List */}
              <div className={`pt-3 mt-2 space-y-1.5 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                {step.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <CheckCircleOutlined className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Trust Badges - Compact */}
      <div className={`flex flex-wrap justify-center gap-4 pt-6 mt-6 border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
        <div className="flex items-center gap-1.5">
          <CheckCircleOutlined className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>10M+ Happy Customers</span>
        </div>
        <div className="flex items-center gap-1.5">
          <SafetyOutlined className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>100% Data Security</span>
        </div>
        <div className="flex items-center gap-1.5">
          <ThunderboltOutlined className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>Instant Payment</span>
        </div>
        <div className="flex items-center gap-1.5">
          <StarOutlined className={`text-sm ${isDarkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>4.8 Rating</span>
        </div>
      </div>
    </div>
  );
};